import { Box } from '@mui/material';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ReactComponent as DownloadIconV2 } from '../../assets/images/icon-downloadV2.svg';
import { AsyncButton } from '../../components/forms';
import InventoryGridView from '../../components/InventoryComponent/InventoryGridView/InventoryGridView';
import InventoryListView from '../../components/InventoryComponent/InventoryListView/InventoryListView';
import { DeleteItemModal } from '../../components/modals/CommonModals';
import FailedUploadInventoryModal from '../../components/modals/FailedUploadInventoryModal/FailedUploadInventoryModal';
import UploadInventoryModal from '../../components/modals/UploadInventoryModal/UploadInventoryModal';
import { IPaginatedListChangeEvent, IPaginatedListRef } from '../../components/PaginatedList/PaginatedList';
import { AddItemButton, PageHeader } from '../../components/_mui';
import {
  CSV_UPLOAD_SUCCESS,
  InventorySuccessToastMessages,
  INVENTORY_ITEM_LABEL,
  MAX_INVENTORY_ITEMS_PER_PAGE,
} from '../../constants/inventories';
import { routes, URL_VARS } from '../../constants/routing';
import { INITIAL_PAGE } from '../../constants/table';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import useModal from '../../hooks/useModal';
import {
  addInventoryItemValue,
  deleteInventoryItemRequest,
  downloadInventoryReportCSVRequest,
  setInventoryItemType,
  uploadInventoryCSVRequest,
} from '../../store/actions/inventories';
import { toggleDownloadSuccessModal } from '../../store/actions/modals';
import { selectIsSuperAdmin } from '../../store/selectors/auth';
import { selectInventoryBulkResult, selectIsInventoryReportLoading } from '../../store/selectors/inventories';
import {
  IInventoryItem,
  InventoryTypesEnum,
  IUploadInventoryCSVRequestPayload,
  IUploadInventoryCSVResponsePayload,
} from '../../types/inventories';
import { IDeleteModalProps } from '../../types/modals';
import { IPromiseCallbacks } from '../../types/redux';
import { IApiError, IDownloadCSVSuccessPayload, NotificationListEnum, ViewTypesEnum } from '../../types/shell';
import notification from '../../utils/notification';
import { handleApiError } from '../../utils/ui';
import useFilters from './Filters/useFilters';

import styles from './InventoryContainer.module.scss';

const InventoryContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { setHeader } = useHeaderDispatch();
  const [currentPageSize, setCurrentPageSize] = useState(MAX_INVENTORY_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const paginatedListRef = useRef<IPaginatedListRef<IInventoryItem>>(null);

  const canEditInventory = useSelector(selectIsSuperAdmin);
  const isInventoryReportLoading = useSelector(selectIsInventoryReportLoading);
  const result = useSelector(selectInventoryBulkResult);

  const {
    openModal: openUploadCSVModal,
    closeModal: closeUploadCSVModal,
    Modal: UploadCSVModal,
  } = useModal({ resetStyles: true });

  const {
    openModal: openFailedUploadCSVModal,
    closeModal: closeFailedUploadCSVModal,
    Modal: FailedUploadCSVModal,
  } = useModal();

  const {
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    Modal: DeleteModal,
  } = useModal<IDeleteModalProps>();

  const downloadInventoryCSV = useCallback(
    (payload: IPromiseCallbacks) => {
      dispatch(downloadInventoryReportCSVRequest(payload));
    },
    [dispatch],
  );

  const handleDownloadClick = useCallback(() => {
    const downloadInventoryCSVPromise = new Promise<IDownloadCSVSuccessPayload>((resolve, reject) => {
      downloadInventoryCSV({ resolve, reject });
    }).then((response: IDownloadCSVSuccessPayload) => {
      dispatch(toggleDownloadSuccessModal(response));
    });

    notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
      promise: downloadInventoryCSVPromise,
      promiseParams: {
        pending: "Downloading process started. We'll notify you when the file is ready!",
        error: {
          render: ({ data: error }) =>
            (error && error.message) || "Something bad has happened. CSV file wasn't created",
        },
      },
    });
  }, [downloadInventoryCSV, dispatch]);

  const renderDownloadButton = useCallback(() => {
    return (
      <AsyncButton
        outlined
        disabled={isInventoryReportLoading}
        className={styles.downloadBtn}
        loaderClassName={styles.spinner}
        isLoading={isInventoryReportLoading}
        onClick={handleDownloadClick}
        dataAction="inventory_download_report_button"
      >
        <DownloadIconV2 className={styles.icon} />
        <div>Download report</div>
      </AsyncButton>
    );
  }, [isInventoryReportLoading, handleDownloadClick]);

  const handleAddNewItem = React.useCallback(
    (type: InventoryTypesEnum) => {
      dispatch(setInventoryItemType(type));
      history.push(routes.inventory.getInventoryUrl({ flowId: URL_VARS.NEW }));
    },
    [history, dispatch],
  );

  const renderAddButton = useCallback(() => {
    return canEditInventory ? (
      <AddItemButton
        onAddNewItem={handleAddNewItem}
        onCsvUpload={openUploadCSVModal}
        dataAction="inventory_add_new_item_button"
      />
    ) : null;
  }, [handleAddNewItem, openUploadCSVModal, canEditInventory]);

  const uploadInventoryCSV = React.useCallback(
    (payload: IUploadInventoryCSVRequestPayload) => {
      dispatch(uploadInventoryCSVRequest(payload));
    },
    [dispatch],
  );

  const showResult = React.useCallback(
    (response: IUploadInventoryCSVResponsePayload) => {
      if (response.error_file_url === '') {
        return notification.success(NotificationListEnum.Success, { content: `Inventory was successfully uploaded!` });
      }
      if (response.error_file_url !== '') {
        openFailedUploadCSVModal();
      }
    },
    [openFailedUploadCSVModal],
  );

  const handleUploadInventory = React.useCallback(
    (fileName: string, fcId: string) => {
      const uploadInventoryCSVPromise = new Promise<IUploadInventoryCSVResponsePayload>((resolve, reject) => {
        uploadInventoryCSV({ file_name: fileName, fc_id: fcId, resolve, reject });
      }).then((response: IUploadInventoryCSVResponsePayload) => {
        if (response.error_file_url) {
          showResult(response);
        } else {
          notification.success(NotificationListEnum.Success, {
            content: InventorySuccessToastMessages[CSV_UPLOAD_SUCCESS],
          });
        }
      });

      notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
        promise: uploadInventoryCSVPromise,
        promiseParams: {
          pending: "Uploading process started. We'll notify you when inventory will be uploaded!",
          error: {
            render: ({ data: error }) =>
              (error && error.message) || "Something bad has happened. Inventory wasn't uploaded",
          },
        },
      });
    },
    [showResult, uploadInventoryCSV],
  );

  const uploadInventoryModal = React.useMemo(
    () => (
      <UploadCSVModal className={cn('common-modal', styles.modalContainer)}>
        {() => {
          return <UploadInventoryModal onClose={closeUploadCSVModal} onSubmit={handleUploadInventory} />;
        }}
      </UploadCSVModal>
    ),
    [UploadCSVModal, closeUploadCSVModal, handleUploadInventory],
  );

  const failedInventoryUpload = React.useMemo(
    () => (
      <FailedUploadCSVModal className={cn('common-modal', styles.modalContainer)}>
        {() => {
          return (
            <FailedUploadInventoryModal
              onClose={closeFailedUploadCSVModal}
              errorFileUrl={result?.error_file_url || ''}
            />
          );
        }}
      </FailedUploadCSVModal>
    ),
    [FailedUploadCSVModal, closeFailedUploadCSVModal, result],
  );

  const downloadButton = useMemo(renderDownloadButton, [renderDownloadButton]);
  const addButton = useMemo(renderAddButton, [renderAddButton]);

  const {
    filteredItems,
    query,
    inventoryTotalCount,
    isInventoryFetching,
    filtersRef,
    Filters,
    fetchInventory,
    activeView,
    isFinalInventoryItem,
    filters,
  } = useFilters({ currentPageSize, renderDownloadButton: downloadButton, renderAddButton: addButton });

  const handlePaginationChange = useCallback(
    ({ page, pageSize, setPage, setPageSize }: IPaginatedListChangeEvent) => {
      const pageSizeToUse = pageSize || currentPageSize;

      fetchInventory({
        page,
        page_size: pageSizeToUse,
      });
      setPage?.(page);
      setPageSize?.(pageSizeToUse);
      setCurrentPage(page);
      setCurrentPageSize(pageSizeToUse);
    },

    [currentPageSize, fetchInventory],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize);
      setCurrentPage(INITIAL_PAGE);
      fetchInventory({
        page: INITIAL_PAGE,
        page_size: newPageSize,
      });
      if (paginatedListRef.current) {
        paginatedListRef.current.resetPageCount();
      }
    },
    [fetchInventory],
  );

  const handleItemEdit = useCallback(
    (item: IInventoryItem) => {
      dispatch(addInventoryItemValue(item));
      history.push(routes.inventory.getInventoryUrl({ flowId: URL_VARS.EDIT, itemId: item.item_id }));
    },
    [dispatch, history],
  );

  const handleItemClick = useCallback(
    (item: IInventoryItem) => {
      if (canEditInventory) {
        handleItemEdit(item);
      } else {
        dispatch(addInventoryItemValue(item));
        history.push(routes.inventory.getInventoryUrl({ flowId: URL_VARS.VIEW, itemId: item.item_id }));
      }
    },
    [handleItemEdit, history, canEditInventory, dispatch],
  );

  const handleDeleteItem = React.useCallback(
    (itemId: string, cb?: () => void) => {
      return new Promise((resolve, reject) => {
        dispatch(deleteInventoryItemRequest({ itemId, resolve, reject }));
      })
        .then(() => {
          notification.success(NotificationListEnum.Success, { content: 'Item was successfully deleted' });
          if (cb) {
            cb();
          }
        })
        .catch(handleApiError(`Something bad happened. The item wasn't deleted.`))
        .finally(() => {
          if (typeof closeDeleteModal === 'function') {
            closeDeleteModal();
          }
        });
    },
    [dispatch, closeDeleteModal],
  );

  const openDeleteDialog = useCallback(
    (id: string) => {
      openDeleteModal({
        id,
        cb: () =>
          handlePaginationChange({
            page: paginatedListRef.current?.page || INITIAL_PAGE,
            setPage: paginatedListRef.current?.changePage!,
          }),
      });
    },
    [handlePaginationChange, openDeleteModal],
  );

  const deleteItemModal = React.useMemo(
    () => (
      <DeleteModal className="common-modal">
        {({ id, cb }) => {
          return (
            <DeleteItemModal
              assetName={INVENTORY_ITEM_LABEL}
              onDelete={() => handleDeleteItem(id, cb)}
              onClose={closeDeleteModal}
            />
          );
        }}
      </DeleteModal>
    ),
    [closeDeleteModal, DeleteModal, handleDeleteItem],
  );

  useEffect(() => {
    if (typeof setHeader === 'function') {
      setHeader({ title: 'View inventory', action: () => history.push(routes.dashboard) });
    }
  }, [history, setHeader]);

  // resets the page to 1 when filters change
  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);

    paginatedListRef.current?.resetPageCount();
  }, [filters]);

  return (
    <>
      <Box sx={{ width: '100%', display: 'flex', flexFlow: 'column' }}>
        <PageHeader>{Filters}</PageHeader>
        {activeView === ViewTypesEnum.List ? (
          <InventoryListView
            onChange={handlePaginationChange}
            items={filteredItems || []}
            totalCount={inventoryTotalCount}
            isLoading={isInventoryFetching}
            searchQuery={query}
            ref={filtersRef}
            canEditInventory={canEditInventory}
            onDelete={openDeleteDialog}
            onEdit={handleItemEdit}
            page={currentPage}
            pageSize={currentPageSize}
          />
        ) : (
          <InventoryGridView
            totalCount={inventoryTotalCount}
            items={filteredItems || []}
            onChange={handlePaginationChange}
            ref={paginatedListRef}
            isFinalItems={isFinalInventoryItem}
            isLoading={isInventoryFetching}
            canEditInventory={canEditInventory}
            onItemClick={handleItemClick}
            onEditClick={handleItemEdit}
            onDeleteClick={openDeleteDialog}
            filtersRef={filtersRef}
            pageSize={currentPageSize}
            onPageSizeChange={handlePageSizeChange}
            page={currentPage}
          />
        )}
      </Box>
      {uploadInventoryModal}
      {failedInventoryUpload}
      {deleteItemModal}
    </>
  );
};

export default InventoryContainer;
